import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@sakhtar/design-system/react';
import blackLogoUrl from '@sakhtar/design-system/assets/logo/sakhtar-black-logo.svg';
import { addDays, formatJalali, systemClock } from '@sakhtar/calendar';
import '../onboarding-shell.css';
import { ApiError } from '../../lib/apiClient.js';
import { login } from '../../lib/auth.js';
import { toEnglishDigits } from '../../lib/phoneNumber.js';
import {
  INDUSTRY_OPTIONS,
  LAUNCH_TRIAL_DURATION_DAYS,
  LOGO_ALLOWED_MIME_TYPES,
  LOGO_MAX_BYTES,
  checkSlugAvailability,
  requestPublicTrialOtp,
  startPublicTrial,
  verifyPublicTrialOtp,
  type IndustryCode,
  type TeamSizeCode,
} from '../../lib/publicApi.js';

// UI_REFERENCE_SOURCE:
// section: Sakhtar Onboarding Desktop.dc.html (claude.ai/design project
//   6c9c527c…, read via DesignSync) — screens A1–A10 plus A4b: intro ->
//   trial confirmation -> mobile -> SMS code -> password -> owner -> company
//   -> workspace address -> about the company -> provisioning -> done.
// status: PARTIAL_REAL
// reconciliation:
//   - A4: 6 code cells, not the prototype's 5 — the backend OTP is 6 digits.
//     The backend does not distinguish "wrong" from "expired", so both share
//     one message.
//   - A4b: the prototype's note "ورود با کد پیامکی هم فعال می‌ماند" is not shown:
//     tenant login's SMS code is a second factor after the password
//     (/login/otp), never a password-free alternative, so the note box
//     states what is true instead.
//   - A6: the logo is read locally and sent with the final create request —
//     there is no separate upload, so no upload-progress state is shown.
//     Limits are the real backend ones (PNG/JPEG/SVG, 500 KB).
//   - A8: industry chips use the backend's fixed industry list; the member
//     count is a number, mapped to the backend's team-size range.
//   - A9: one real request creates the workspace — no timer-driven fake
//     progress; the first step shows as running until the request returns.
//   - Prototype-only affordances (state-injection toolbar, design notes,
//     read-only mode, resume banner, "مرور فلو از ابتدا") are not rendered.

type Screen = 'A1' | 'A2' | 'A3' | 'A4' | 'A4b' | 'A5' | 'A6' | 'A7' | 'A8' | 'A9' | 'A10';
type OtpState = 'idle' | 'verifying' | 'verified' | 'incomplete' | 'invalid' | 'rate' | 'net';
type SlugCheck = 'idle' | 'invalid' | 'checking' | 'available' | 'taken';

const RAIL: readonly { readonly screen: Screen; readonly label: string }[] = [
  { screen: 'A3', label: 'شماره موبایل' },
  { screen: 'A4', label: 'تأیید پیامکی' },
  { screen: 'A4b', label: 'رمز عبور' },
  { screen: 'A5', label: 'اطلاعات شما' },
  { screen: 'A6', label: 'شرکت شما' },
  { screen: 'A7', label: 'آدرس فضای کاری' },
  { screen: 'A8', label: 'درباره شرکت' },
];

const MOBILE_SHAPE = /^09\d{9}$/;
const SLUG_SHAPE = /^[a-z0-9](?:[a-z0-9-]{0,38}[a-z0-9])?$/;
const OTP_LENGTH = 6;
const MIN_PASSWORD_LENGTH = 8;
const RESEND_SECONDS = 60;
const SLUG_PREFIX = 'cloud.sakhtar.net/t/';

const TILE_BRAND = { tint: '#dcdffb', ink: '#4a52c4', glow: 'rgba(76,83,196,.32)' };
const TILE_INFO = { tint: '#d5e8fb', ink: '#2f6fa8', glow: 'rgba(47,111,168,.30)' };
const TILE_SUCCESS = { tint: '#d6f0e2', ink: '#2e7d5b', glow: 'rgba(46,125,91,.28)' };
const TILE_DANGER = { tint: '#fbdcdc', ink: '#b04141', glow: 'rgba(176,65,65,.28)' };

const PROVISION_STEPS = ['ثبت اطلاعات سازمان', 'ایجاد فضای کاری', 'آماده‌سازی تنظیمات اولیه'];

const OTP_MESSAGES: Partial<Record<OtpState, string>> = {
  incomplete: 'کد ۶ رقمی را کامل وارد کنید.',
  invalid: 'کد وارد‌شده صحیح نیست یا اعتبار آن به پایان رسیده است.',
  rate: 'درخواست کد بیش از حد مجاز بوده است. چند دقیقه دیگر تلاش کنید.',
  net: 'بررسی کد انجام نشد. اتصال شما قطع است یا سرویس پاسخ نمی‌دهد.',
  verified: 'شماره موبایل تأیید شد.',
};

const SLUG_MESSAGES: Partial<Record<SlugCheck, string>> = {
  checking: 'در حال بررسی…',
  taken: 'این آدرس فضای کاری قبلاً انتخاب شده است.',
  invalid: 'فقط حروف انگلیسی کوچک، عدد و خط تیره مجاز است.',
  available: 'این آدرس در دسترس است.',
};

function fa(value: string | number): string {
  return String(value).replace(/[0-9]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)] ?? d);
}

function teamSizeRangeFor(count: number): TeamSizeCode {
  if (count <= 5) return '1-5';
  if (count <= 20) return '6-20';
  if (count <= 50) return '21-50';
  if (count <= 200) return '51-200';
  return '200+';
}

function tileStyle(
  size: number,
  radius: number,
  tone: { tint: string; ink: string; glow: string },
): CSSProperties {
  return {
    width: size,
    height: size,
    borderRadius: radius,
    background: `linear-gradient(158deg, #ffffff 8%, ${tone.tint} 100%)`,
    color: tone.ink,
    display: 'grid',
    placeContent: 'center',
    boxShadow: `0 8px 16px -6px ${tone.glow}, inset 0 1.5px 0 rgba(255,255,255,.95), inset 0 -3px 6px rgba(15,18,25,.05)`,
    border: '1px solid rgba(255,255,255,.7)',
    flex: '0 0 auto',
    overflow: 'hidden',
  };
}

const h1Style: CSSProperties = { margin: 0, fontSize: 26, fontWeight: 700, color: 'var(--sk-fg)' };
const leadStyle: CSSProperties = {
  margin: 0,
  fontSize: 14,
  lineHeight: 1.95,
  color: 'var(--sk-fg-muted)',
};
const screenStyle: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 20 };
const actionsStyle: CSSProperties = { display: 'flex', alignItems: 'center', gap: 12 };
const dividerTop: CSSProperties = { borderTop: '1px solid var(--sk-divider)' };

function FieldError({ children }: { children: ReactNode }) {
  return <span style={{ fontSize: 12, color: 'var(--sk-danger)' }}>{children}</span>;
}

function FieldHint({ children }: { children: ReactNode }) {
  return (
    <span style={{ fontSize: 12, lineHeight: 1.8, color: 'var(--sk-fg-subtle)' }}>{children}</span>
  );
}

function ScreenHeader({ title, lead }: { title: string; lead?: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <h1 style={h1Style}>{title}</h1>
      {lead ? <p style={leadStyle}>{lead}</p> : null}
    </div>
  );
}

function ErrorBanner({
  title,
  body,
  onRetry,
}: {
  title: string;
  body: string;
  onRetry?: () => void;
}) {
  return (
    <div
      role="alert"
      style={{
        display: 'flex',
        gap: 11,
        alignItems: 'flex-start',
        padding: '14px 16px',
        borderRadius: 'var(--sk-r-md)',
        background: 'var(--sk-danger-soft)',
        border: '1px solid color-mix(in oklch, var(--sk-danger), transparent 78%)',
      }}
    >
      <span style={{ color: 'var(--sk-danger)', display: 'flex', paddingTop: 2, flex: '0 0 auto' }}>
        <Icon name="x" size="sm" />
      </span>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--sk-fg)' }}>{title}</span>
        <span style={{ fontSize: 12, lineHeight: 1.8, color: 'var(--sk-fg-muted)' }}>{body}</span>
      </div>
      {onRetry ? (
        <button type="button" className="sk-btn sk-btn--secondary sk-btn--sm" onClick={onRetry}>
          تلاش مجدد
        </button>
      ) : null}
    </div>
  );
}

function StepDot({ state }: { state: 'done' | 'current' | 'running' | 'pending' }) {
  if (state === 'done') {
    return (
      <span
        style={{
          width: 22,
          height: 22,
          borderRadius: '50%',
          background: 'var(--sk-success)',
          color: '#fff',
          display: 'grid',
          placeContent: 'center',
          flex: '0 0 auto',
          fontSize: 13,
        }}
      >
        <Icon name="check" size="sm" />
      </span>
    );
  }
  if (state === 'current') {
    return (
      <span
        style={{
          width: 22,
          height: 22,
          borderRadius: '50%',
          border: '2px solid var(--sk-brand-500)',
          display: 'grid',
          placeContent: 'center',
          flex: '0 0 auto',
        }}
      >
        <span
          style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--sk-brand-500)' }}
        />
      </span>
    );
  }
  if (state === 'running') {
    return <span className="sk-spin" style={{ width: 22, height: 22, flex: '0 0 auto' }} />;
  }
  return (
    <span
      style={{
        width: 22,
        height: 22,
        borderRadius: '50%',
        border: '1.5px solid var(--sk-border-strong)',
        flex: '0 0 auto',
      }}
    />
  );
}

interface CreatedWorkspace {
  readonly slug: string;
  readonly displayName: string;
  readonly ownerName: string;
  readonly nextPath: string;
  readonly trialEndsLabel: string;
}

export function TrialOnboardingPage() {
  const navigate = useNavigate();
  const [screen, setScreen] = useState<Screen>('A1');

  // A3
  const [phone, setPhone] = useState('');
  const [phoneErr, setPhoneErr] = useState<string | null>(null);
  const [rateLimited, setRateLimited] = useState(false);
  const [netErr, setNetErr] = useState(false);
  const [sending, setSending] = useState(false);
  const [flowNotice, setFlowNotice] = useState<string | null>(null);

  // A4
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [otp, setOtp] = useState('');
  const [otpState, setOtpState] = useState<OtpState>('idle');
  const [otpFocused, setOtpFocused] = useState(false);
  const [resendLeft, setResendLeft] = useState(RESEND_SECONDS);
  const [otpProof, setOtpProof] = useState<string | null>(null);

  // A4b
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errPw, setErrPw] = useState(false);
  const [errPw2, setErrPw2] = useState(false);

  // A5
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [ownerRole, setOwnerRole] = useState('');
  const [ownerErrors, setOwnerErrors] = useState<Record<string, string>>({});

  // A6
  const [company, setCompany] = useState('');
  const [brandName, setBrandName] = useState('');
  const [errCompany, setErrCompany] = useState(false);
  const [logo, setLogo] = useState<{ dataUrl: string; bytes: number } | null>(null);
  const [logoError, setLogoError] = useState<string | null>(null);
  const logoInputRef = useRef<HTMLInputElement | null>(null);

  // A7
  const [slug, setSlug] = useState('');
  const [slugCheck, setSlugCheck] = useState<SlugCheck>('idle');
  const [slugSuggestions, setSlugSuggestions] = useState<readonly string[]>([]);

  // A8
  const [industries, setIndustries] = useState<readonly IndustryCode[]>([]);
  const [memberCount, setMemberCount] = useState('');
  const [errMembers, setErrMembers] = useState(false);

  // A9 / A10
  const [provFailed, setProvFailed] = useState<string | null>(null);
  const [created, setCreated] = useState<CreatedWorkspace | null>(null);
  // A rejected request is never retried as-is, and the backend binds a key
  // to the exact request body it first saw — so a key is renewed after a
  // rejection, and kept for a retry after a network/server failure (where
  // replaying the same request is exactly what the key is for).
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());

  const go = (next: Screen) => {
    setFlowNotice(null);
    setScreen(next);
  };

  useEffect(() => {
    if (screen !== 'A4' || resendLeft <= 0) return;
    const timer = setInterval(() => setResendLeft((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(timer);
  }, [screen, resendLeft]);

  // Live availability, debounced; a malformed address never hits the network.
  useEffect(() => {
    if (screen !== 'A7') return;
    setSlugSuggestions([]);
    if (slug === '') {
      setSlugCheck('idle');
      return;
    }
    if (!SLUG_SHAPE.test(slug)) {
      setSlugCheck('invalid');
      return;
    }
    setSlugCheck('checking');
    let cancelled = false;
    const timer = setTimeout(() => {
      checkSlugAvailability(slug)
        .then(async (result) => {
          if (cancelled) return;
          if (result.available) {
            setSlugCheck('available');
            return;
          }
          setSlugCheck('taken');
          const candidates = [`${slug}-co`, `${slug}-sys`, `${slug}1`].filter((c) =>
            SLUG_SHAPE.test(c),
          );
          const checked = await Promise.all(
            candidates.map((c) =>
              checkSlugAvailability(c)
                .then((r) => (r.available ? c : null))
                .catch(() => null),
            ),
          );
          if (!cancelled) setSlugSuggestions(checked.filter((c): c is string => c !== null));
        })
        .catch(() => {
          if (!cancelled) setSlugCheck('idle');
        });
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [screen, slug]);

  async function sendCode() {
    const normalized = toEnglishDigits(phone).trim();
    setNetErr(false);
    setRateLimited(false);
    if (!MOBILE_SHAPE.test(normalized)) {
      setPhoneErr('این شماره موبایل معتبر نیست.');
      return;
    }
    setPhoneErr(null);
    setSending(true);
    try {
      const challenge = await requestPublicTrialOtp(normalized);
      setPhone(normalized);
      setChallengeId(challenge.challengeId);
      setOtp('');
      setOtpState('idle');
      setOtpProof(null);
      setResendLeft(RESEND_SECONDS);
      go('A4');
    } catch (err) {
      if (err instanceof ApiError && err.code === 'VALIDATION_FAILED') {
        const alreadyUsed = err.envelope.error.details?.some(
          (d) => d.path === 'ownerLoginIdentifier' && d.message.includes('ثبت شده'),
        );
        setPhoneErr(
          alreadyUsed
            ? 'این شماره موبایل قبلاً برای ساخت فضای کاری استفاده شده است. لطفاً وارد شوید.'
            : 'این شماره موبایل معتبر نیست.',
        );
      } else if (err instanceof ApiError && err.code === 'RATE_LIMITED') {
        setRateLimited(true);
      } else {
        setNetErr(true);
      }
    } finally {
      setSending(false);
    }
  }

  async function resendCode() {
    setOtpState('idle');
    try {
      const challenge = await requestPublicTrialOtp(phone);
      setChallengeId(challenge.challengeId);
      setOtp('');
      setResendLeft(RESEND_SECONDS);
    } catch (err) {
      setOtpState(err instanceof ApiError && err.code === 'RATE_LIMITED' ? 'rate' : 'net');
    }
  }

  async function verifyCode() {
    if (!challengeId || otp.length !== OTP_LENGTH) {
      setOtpState('incomplete');
      return;
    }
    setOtpState('verifying');
    try {
      const verified = await verifyPublicTrialOtp({
        ownerLoginIdentifier: phone,
        challengeId,
        otp,
      });
      setOtpProof(verified.otpProof);
      setOtpState('verified');
      go('A4b');
    } catch (err) {
      if (err instanceof ApiError && err.code === 'VALIDATION_FAILED') setOtpState('invalid');
      else if (err instanceof ApiError && err.code === 'RATE_LIMITED') setOtpState('rate');
      else setOtpState('net');
    }
  }

  function submitPassword() {
    if (passwordRules.some((r) => !r.ok)) {
      setErrPw(true);
      return;
    }
    if (passwordConfirmation !== password) {
      setErrPw(false);
      setErrPw2(true);
      return;
    }
    setErrPw(false);
    setErrPw2(false);
    go('A5');
  }

  function submitOwner() {
    const errors: Record<string, string> = {};
    if (firstName.trim() === '') errors['firstName'] = 'نام الزامی است.';
    if (lastName.trim() === '') errors['lastName'] = 'نام خانوادگی الزامی است.';
    setOwnerErrors(errors);
    if (Object.keys(errors).length === 0) go('A6');
  }

  function submitCompany() {
    if (company.trim() === '') {
      setErrCompany(true);
      return;
    }
    setErrCompany(false);
    go('A7');
  }

  function pickLogo(file: File | undefined) {
    setLogoError(null);
    if (!file) return;
    if (!(LOGO_ALLOWED_MIME_TYPES as readonly string[]).includes(file.type)) {
      setLogoError('فرمت تصویر باید PNG، JPEG، SVG یا WEBP باشد.');
      return;
    }
    if (file.size > LOGO_MAX_BYTES) {
      setLogoError(
        `حجم تصویر نباید بیشتر از ${fa(Math.floor(LOGO_MAX_BYTES / 1_000_000))} مگابایت باشد.`,
      );
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') setLogo({ dataUrl: reader.result, bytes: file.size });
    };
    reader.onerror = () => setLogoError('خواندن فایل تصویر ناموفق بود.');
    reader.readAsDataURL(file);
  }

  /** Sends the user back to the screen that owns a rejected field. */
  function routeRejection(err: ApiError) {
    const paths = (err.envelope.error.details ?? []).map((d) => d.path);
    const has = (...names: string[]) => paths.some((p) => names.includes(p));
    if (has('slug')) {
      setSlugCheck('taken');
      go('A7');
      setFlowNotice('این آدرس فضای کاری در دسترس نیست. آدرس دیگری انتخاب کنید.');
    } else if (has('otpProof', 'ownerLoginIdentifier')) {
      setOtpProof(null);
      go('A3');
      setFlowNotice('تأیید پیامکی منقضی شده است. لطفاً دوباره کد تأیید دریافت کنید.');
    } else if (has('ownerPassword', 'ownerPasswordConfirmation')) {
      go('A4b');
      setFlowNotice('رمز عبور پذیرفته نشد. لطفاً رمز دیگری انتخاب کنید.');
    } else if (has('ownerFirstName', 'ownerLastName', 'ownerRole')) {
      go('A5');
      setFlowNotice('بعضی از اطلاعات شما معتبر نیست. لطفاً بررسی و اصلاح کنید.');
    } else if (has('organizationName', 'brandName', 'logoDataUrl')) {
      go('A6');
      setFlowNotice('اطلاعات شرکت معتبر نیست. لطفاً بررسی و اصلاح کنید.');
    } else if (has('industries', 'teamSizeRange')) {
      go('A8');
      setFlowNotice('اطلاعات این مرحله معتبر نیست. لطفاً بررسی و اصلاح کنید.');
    } else {
      setProvFailed('اطلاعات ارسال‌شده پذیرفته نشد. لطفاً اطلاعات را بررسی کنید.');
    }
  }

  async function createWorkspace() {
    const count = Number(memberCount);
    if (memberCount === '' || !Number.isInteger(count) || count < 1) {
      setErrMembers(true);
      return;
    }
    if (!otpProof) {
      go('A3');
      setFlowNotice('تأیید پیامکی منقضی شده است. لطفاً دوباره کد تأیید دریافت کنید.');
      return;
    }
    setErrMembers(false);
    setProvFailed(null);
    go('A9');
    const trimmedSlug = slug.trim();
    try {
      await startPublicTrial(
        {
          organizationName: company.trim(),
          slug: trimmedSlug,
          ownerFirstName: firstName.trim(),
          ownerLastName: lastName.trim(),
          ownerLoginIdentifier: phone,
          ownerPassword: password,
          ownerPasswordConfirmation: passwordConfirmation,
          otpProof,
          teamSizeRange: teamSizeRangeFor(count),
          ...(brandName.trim() === '' ? {} : { brandName: brandName.trim() }),
          ...(ownerRole.trim() === '' ? {} : { ownerRole: ownerRole.trim() }),
          ...(industries.length === 0 ? {} : { industries }),
          ...(logo === null ? {} : { logoDataUrl: logo.dataUrl }),
        },
        idempotencyKey,
      );
    } catch (err) {
      if (err instanceof ApiError && err.code === 'VALIDATION_FAILED') {
        setIdempotencyKey(crypto.randomUUID());
        routeRejection(err);
      } else if (err instanceof ApiError && err.code === 'IDEMPOTENCY_KEY_REUSED') {
        setIdempotencyKey(crypto.randomUUID());
        setProvFailed(
          'اطلاعات سازمان شما ذخیره نشد. با «تلاش دوباره» درخواست دوباره ارسال می‌شود.',
        );
      } else if (err instanceof ApiError && err.code === 'IDEMPOTENCY_KEY_IN_FLIGHT') {
        setProvFailed('ساخت فضای کاری شما هنوز در جریان است. چند لحظه بعد «تلاش دوباره» را بزنید.');
      } else if (err instanceof ApiError && err.code === 'RATE_LIMITED') {
        setProvFailed('تعداد درخواست‌ها زیاد بوده است. چند دقیقه دیگر دوباره تلاش کنید.');
      } else {
        setProvFailed(
          'آماده‌سازی فضای کاری متوقف شد. اطلاعات وارد‌شده از بین نرفته است. با «تلاش دوباره» دوباره تلاش کنید یا با پشتیبانی تماس بگیرید.',
        );
      }
      return;
    }

    // The workspace exists from here on — a failed sign-in must never send
    // the user back into the signup form; it only changes where A10 leads.
    let nextPath = `/t/${trimmedSlug}/login`;
    try {
      const signedIn = await login(trimmedSlug, phone, password);
      if (signedIn.status === 'authenticated') nextPath = signedIn.redirectTo;
    } catch {
      // Keep the login page as the destination.
    }
    const trialEnds = addDays(systemClock.today(), LAUNCH_TRIAL_DURATION_DAYS);
    setCreated({
      slug: trimmedSlug,
      displayName: brandName.trim() || company.trim(),
      ownerName: `${firstName.trim()} ${lastName.trim()}`,
      nextPath,
      trialEndsLabel: formatJalali(trialEnds, { persianDigits: true }),
    });
    go('A10');
  }

  const passwordRules = [
    {
      label: `حداقل ${fa(MIN_PASSWORD_LENGTH)} کاراکتر`,
      ok: password.length >= MIN_PASSWORD_LENGTH,
    },
    { label: 'شامل حرف انگلیسی', ok: /[a-zA-Z]/.test(password) },
    { label: 'شامل عدد', ok: /[0-9]/.test(password) },
  ];
  const pwScore = passwordRules.filter((r) => r.ok).length;
  const pwColor =
    pwScore >= 3 ? 'var(--sk-success)' : pwScore === 2 ? 'var(--sk-warn)' : 'var(--sk-danger)';
  const pwLabel = password === '' ? '—' : pwScore >= 3 ? 'قوی' : pwScore === 2 ? 'متوسط' : 'ضعیف';
  const pwMatched = passwordConfirmation !== '' && passwordConfirmation === password && !errPw2;
  const pwInputType = showPassword ? 'text' : 'password';

  const railIdx = RAIL.findIndex((r) => r.screen === screen);
  const railVisible = railIdx >= 0;
  const railPct = railVisible ? Math.round((railIdx / RAIL.length) * 100) : 0;
  const otpBad = otpState === 'invalid' || otpState === 'rate' || otpState === 'net';
  const otpMsg = OTP_MESSAGES[otpState];
  const otpMsgColor =
    otpState === 'verified'
      ? 'var(--sk-success)'
      : otpState === 'rate'
        ? 'var(--sk-warn)'
        : 'var(--sk-danger)';
  const slugMsg = SLUG_MESSAGES[slugCheck];
  const slugMsgColor =
    slugCheck === 'available'
      ? 'var(--sk-success)'
      : slugCheck === 'checking'
        ? 'var(--sk-fg-muted)'
        : 'var(--sk-danger)';
  const resendClock = fa(
    `${String(Math.floor(resendLeft / 60)).padStart(2, '0')}:${String(resendLeft % 60).padStart(2, '0')}`,
  );
  const maskedPhone = phone.length >= 7 ? `${phone.slice(0, 4)}••••${phone.slice(-3)}` : phone;

  return (
    <div
      className="sk-app sk-safe-top"
      dir="rtl"
      style={{
        width: '100%',
        height: '100dvh',
        background: 'var(--sk-bg-canvas)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        className="sk-onb-topbar"
        style={{
          flex: '0 0 auto',
          height: 64,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '0 28px',
          background: 'var(--sk-bg-elev)',
          borderBottom: '1px solid var(--sk-border)',
        }}
      >
        <div style={tileStyle(34, 11, TILE_BRAND)}>
          <img src={blackLogoUrl} alt="" style={{ width: 20, height: 20, display: 'block' }} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--sk-fg)' }}>ساختار سیستم</span>
          <span
            className="sk-onb-topbar-subtitle"
            style={{ fontSize: 11, color: 'var(--sk-fg-subtle)' }}
          >
            ساخت فضای کاری
          </span>
        </div>
        {railVisible ? (
          <span className="sk-badge sk-badge--neutral" style={{ marginInlineStart: 14 }}>
            مرحله {fa(railIdx + 1)} از {fa(RAIL.length)}
          </span>
        ) : null}
        <div style={{ marginInlineStart: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span
            className="sk-onb-topbar-subtitle"
            style={{ fontSize: 13, color: 'var(--sk-fg-muted)' }}
          >
            قبلاً حساب دارید؟
          </span>
          <button
            type="button"
            className="sk-btn sk-btn--secondary sk-btn--sm"
            onClick={() => navigate('/start/login')}
          >
            ورود
          </button>
        </div>
      </div>

      {railVisible ? (
        <div
          className="sk-onb-mobile-progress"
          style={{
            display: 'none',
            flexDirection: 'column',
            gap: 6,
            padding: '10px 16px',
            background: 'var(--sk-bg-elev)',
            borderBottom: '1px solid var(--sk-border)',
          }}
        >
          <div className="sk-prog">
            <div className="sk-prog__fill" style={{ width: `${railPct}%` }} />
          </div>
          <span style={{ fontSize: 11.5, color: 'var(--sk-fg-muted)' }}>
            {fa(railPct)}٪ تکمیل شده
          </span>
        </div>
      ) : null}

      <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
        {railVisible ? (
          <div
            className="sk-onb-rail"
            style={{
              flex: '0 0 296px',
              background: 'var(--sk-bg-elev)',
              borderInlineStart: '1px solid var(--sk-border)',
              padding: '30px 26px',
              display: 'flex',
              flexDirection: 'column',
              gap: 20,
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '.05em',
                  color: 'var(--sk-fg-subtle)',
                }}
              >
                فاز ۱ از ۳
              </span>
              <span style={{ fontSize: 18, fontWeight: 600, color: 'var(--sk-fg)' }}>
                ساخت حساب و فضای کاری
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {RAIL.map((r, i) => (
                <div
                  key={r.screen}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, height: 42 }}
                >
                  <StepDot state={i < railIdx ? 'done' : i === railIdx ? 'current' : 'pending'} />
                  <span
                    style={{
                      fontSize: 14,
                      fontWeight: i === railIdx ? 600 : 400,
                      color:
                        i === railIdx
                          ? 'var(--sk-fg)'
                          : i < railIdx
                            ? 'var(--sk-fg-muted)'
                            : 'var(--sk-fg-subtle)',
                    }}
                  >
                    {r.label}
                  </span>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 9 }}>
              <div className="sk-prog">
                <div className="sk-prog__fill" style={{ width: `${railPct}%` }} />
              </div>
              <span style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>
                {fa(railPct)}٪ تکمیل شده
              </span>
              <div
                style={{
                  display: 'flex',
                  gap: 9,
                  padding: '12px 14px',
                  borderRadius: 'var(--sk-r-md)',
                  background: 'var(--sk-info-soft)',
                  alignItems: 'flex-start',
                  marginTop: 4,
                }}
              >
                <span
                  style={{ color: '#2f6fa8', display: 'flex', paddingTop: 2, flex: '0 0 auto' }}
                >
                  <Icon name="shield" size="sm" />
                </span>
                <span style={{ fontSize: 12, lineHeight: 1.8, color: 'var(--sk-fg-muted)' }}>
                  دوره آزمایشی {fa(LAUNCH_TRIAL_DURATION_DAYS)} روزه بعد از ساخت فضای کاری آغاز
                  می‌شود، نه اکنون.
                </span>
              </div>
            </div>
          </div>
        ) : null}

        <div
          className="sk-onb-content"
          style={{
            flex: 1,
            minWidth: 0,
            overflow: 'auto',
            display: 'flex',
            justifyContent: 'center',
            padding: '44px 32px 60px',
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: 720,
              display: 'flex',
              flexDirection: 'column',
              gap: 20,
            }}
          >
            {flowNotice ? <ErrorBanner title="نیاز به بررسی" body={flowNotice} /> : null}

            {screen === 'A1' ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 26, paddingTop: 18 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <span className="sk-badge sk-badge--brand" style={{ alignSelf: 'flex-start' }}>
                    راه‌اندازی ERP اختصاصی
                  </span>
                  <h1
                    style={{
                      margin: 0,
                      fontSize: 34,
                      lineHeight: 1.5,
                      fontWeight: 700,
                      color: 'var(--sk-fg)',
                      textWrap: 'pretty',
                    }}
                  >
                    ERP ساختار را برای شرکت خودتان راه‌اندازی کنید
                  </h1>
                  <p
                    style={{
                      margin: 0,
                      fontSize: 15,
                      lineHeight: 2,
                      color: 'var(--sk-fg-muted)',
                      maxWidth: 600,
                      textWrap: 'pretty',
                    }}
                  >
                    فروش، انبار، مالی، پروژه‌ها و بخش‌های اصلی شرکت شما در یک فضای کاری یکپارچه
                    آماده می‌شوند.
                  </p>
                </div>
                <div
                  className="sk-onb-grid3"
                  style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}
                >
                  {(
                    [
                      {
                        icon: 'building',
                        label: 'یک فضای کاری اختصاصی برای شرکت شما',
                        tone: TILE_BRAND,
                      },
                      {
                        icon: 'settings',
                        label: 'اطلاعات و تنظیمات متناسب با کسب‌وکار شما',
                        tone: TILE_INFO,
                      },
                      {
                        icon: 'calendar',
                        label: `${fa(LAUNCH_TRIAL_DURATION_DAYS)} روز استفاده آزمایشی`,
                        tone: TILE_SUCCESS,
                      },
                    ] as const
                  ).map((b) => (
                    <div
                      key={b.icon}
                      className="sk-card"
                      style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}
                    >
                      <span style={tileStyle(44, 14, b.tone)}>
                        <Icon name={b.icon} size="md" />
                      </span>
                      <span
                        style={{
                          fontSize: 14,
                          lineHeight: 1.85,
                          fontWeight: 500,
                          color: 'var(--sk-fg)',
                        }}
                      >
                        {b.label}
                      </span>
                    </div>
                  ))}
                </div>
                <div style={{ ...actionsStyle, paddingTop: 4 }}>
                  <button
                    type="button"
                    className="sk-btn sk-btn--primary sk-btn--lg"
                    onClick={() => go('A2')}
                  >
                    شروع ثبت‌نام
                  </button>
                  <button
                    type="button"
                    className="sk-btn sk-btn--ghost sk-btn--lg"
                    onClick={() => navigate('/start')}
                  >
                    بازگشت
                  </button>
                </div>
              </div>
            ) : null}

            {screen === 'A2' ? (
              <div style={{ ...screenStyle, paddingTop: 36, maxWidth: 560 }}>
                <h1 style={h1Style}>شروع دوره آزمایشی</h1>
                <div
                  className="sk-card"
                  style={{ padding: 24, display: 'flex', gap: 14, alignItems: 'flex-start' }}
                >
                  <span style={tileStyle(42, 13, TILE_BRAND)}>
                    <Icon name="calendar" size="sm" />
                  </span>
                  <p style={{ margin: 0, fontSize: 15, lineHeight: 2.1, color: 'var(--sk-fg)' }}>
                    پس از تکمیل ثبت‌نام و ساخت فضای کاری، دوره آزمایشی{' '}
                    {fa(LAUNCH_TRIAL_DURATION_DAYS)} روزه شما آغاز می‌شود.
                  </p>
                </div>
                <div style={actionsStyle}>
                  <button
                    type="button"
                    className="sk-btn sk-btn--primary sk-btn--lg"
                    onClick={() => go('A3')}
                  >
                    تأیید و ادامه
                  </button>
                  <button
                    type="button"
                    className="sk-btn sk-btn--ghost sk-btn--lg"
                    onClick={() => go('A1')}
                  >
                    بازگشت
                  </button>
                </div>
              </div>
            ) : null}

            {screen === 'A3' ? (
              <form
                noValidate
                style={screenStyle}
                onSubmit={(e) => {
                  e.preventDefault();
                  void sendCode();
                }}
              >
                <ScreenHeader
                  title="شماره موبایل شما"
                  lead="این شماره برای تأیید هویت و ورود به حساب مالک سازمان استفاده می‌شود."
                />
                {netErr ? (
                  <ErrorBanner
                    title="ارسال کد انجام نشد"
                    body="ارتباط با سرویس پیامک برقرار نشد. لطفاً چند لحظه بعد دوباره تلاش کنید."
                    onRetry={() => void sendCode()}
                  />
                ) : null}
                <div
                  className="sk-card"
                  style={{
                    padding: 24,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                    maxWidth: 520,
                  }}
                >
                  <div className="sk-field">
                    <label htmlFor="trial-phone">شماره موبایل</label>
                    <input
                      id="trial-phone"
                      className="sk-input"
                      type="tel"
                      inputMode="numeric"
                      autoComplete="tel"
                      maxLength={11}
                      required
                      value={phone}
                      onChange={(e) => {
                        setPhone(e.target.value);
                        setPhoneErr(null);
                      }}
                      placeholder="09xxxxxxxxx"
                      style={{ direction: 'ltr', textAlign: 'left', letterSpacing: '.05em' }}
                    />
                  </div>
                  {phoneErr ? <FieldError>{phoneErr}</FieldError> : null}
                  {rateLimited ? (
                    <span style={{ fontSize: 12, color: 'var(--sk-warn)' }}>
                      تعداد درخواست‌ها زیاد بوده است. چند دقیقه دیگر دوباره تلاش کنید.
                    </span>
                  ) : null}
                </div>
                <div style={actionsStyle}>
                  {sending ? (
                    <button type="button" className="sk-btn sk-btn--primary sk-btn--lg" disabled>
                      <span className="sk-spin" style={{ width: 16, height: 16 }} />
                      در حال ارسال…
                    </button>
                  ) : (
                    <button type="submit" className="sk-btn sk-btn--primary sk-btn--lg">
                      ارسال کد تأیید
                    </button>
                  )}
                  <button
                    type="button"
                    className="sk-btn sk-btn--ghost sk-btn--lg"
                    onClick={() => go('A2')}
                  >
                    بازگشت
                  </button>
                </div>
              </form>
            ) : null}

            {screen === 'A4' ? (
              <form
                noValidate
                style={screenStyle}
                onSubmit={(e) => {
                  e.preventDefault();
                  void verifyCode();
                }}
              >
                <ScreenHeader
                  title="تأیید شماره موبایل"
                  lead={
                    <>
                      کد ارسال‌شده به{' '}
                      <span
                        style={{
                          fontWeight: 600,
                          color: 'var(--sk-fg)',
                          direction: 'ltr',
                          display: 'inline-block',
                        }}
                      >
                        {fa(maskedPhone)}
                      </span>{' '}
                      را وارد کنید.
                    </>
                  }
                />
                <div
                  className="sk-card"
                  style={{
                    padding: 26,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 16,
                    maxWidth: 520,
                  }}
                >
                  <div style={{ position: 'relative', width: 'max-content', maxWidth: '100%' }}>
                    <div style={{ display: 'flex', gap: 10, direction: 'ltr' }}>
                      {Array.from({ length: OTP_LENGTH }, (_, i) => {
                        const active =
                          otpFocused && !otpBad && i === Math.min(otp.length, OTP_LENGTH - 1);
                        return (
                          <div
                            key={i}
                            className="sk-onb-otp-cell"
                            style={{
                              width: 56,
                              height: 60,
                              borderRadius: 'var(--sk-r-md)',
                              border: `1.5px solid ${
                                otpBad
                                  ? 'var(--sk-danger)'
                                  : otpState === 'verified'
                                    ? 'var(--sk-success)'
                                    : active
                                      ? 'var(--sk-brand-500)'
                                      : 'var(--sk-border)'
                              }`,
                              background: otpBad ? 'var(--sk-danger-soft)' : 'var(--sk-bg-canvas)',
                              boxShadow: 'var(--sk-inset-2)',
                              display: 'grid',
                              placeContent: 'center',
                              fontSize: 24,
                              fontWeight: 600,
                              color: 'var(--sk-fg)',
                            }}
                          >
                            {otp[i] ? fa(otp[i]) : ''}
                          </div>
                        );
                      })}
                    </div>
                    <input
                      aria-label="کد تأیید پیامکی"
                      autoComplete="one-time-code"
                      inputMode="numeric"
                      autoFocus
                      maxLength={OTP_LENGTH}
                      value={otp}
                      onFocus={() => setOtpFocused(true)}
                      onBlur={() => setOtpFocused(false)}
                      onChange={(e) => {
                        setOtp(
                          toEnglishDigits(e.target.value).replace(/\D/g, '').slice(0, OTP_LENGTH),
                        );
                        setOtpState('idle');
                      }}
                      style={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        opacity: 0,
                        border: 0,
                        cursor: 'text',
                      }}
                    />
                  </div>
                  {otpMsg ? (
                    <span style={{ fontSize: 13, lineHeight: 1.8, color: otpMsgColor }}>
                      {otpMsg}
                    </span>
                  ) : null}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      ...dividerTop,
                      paddingTop: 16,
                    }}
                  >
                    {resendLeft <= 0 ? (
                      <button
                        type="button"
                        className="sk-btn sk-btn--secondary sk-btn--sm"
                        onClick={() => void resendCode()}
                      >
                        ارسال مجدد کد
                      </button>
                    ) : (
                      <>
                        <span style={{ fontSize: 13, color: 'var(--sk-fg-subtle)' }}>
                          ارسال مجدد کد
                        </span>
                        <span
                          className="sk-num"
                          style={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: 'var(--sk-fg-muted)',
                            direction: 'ltr',
                          }}
                        >
                          {resendClock}
                        </span>
                      </>
                    )}
                  </div>
                </div>
                <div style={actionsStyle}>
                  {otpState === 'verifying' ? (
                    <button type="button" className="sk-btn sk-btn--primary sk-btn--lg" disabled>
                      <span className="sk-spin" style={{ width: 16, height: 16 }} />
                      در حال بررسی…
                    </button>
                  ) : (
                    <button type="submit" className="sk-btn sk-btn--primary sk-btn--lg">
                      تأیید کد
                    </button>
                  )}
                  <button
                    type="button"
                    className="sk-btn sk-btn--ghost sk-btn--lg"
                    onClick={() => go('A3')}
                  >
                    تغییر شماره
                  </button>
                </div>
              </form>
            ) : null}

            {screen === 'A4b' ? (
              <form
                noValidate
                style={screenStyle}
                onSubmit={(e) => {
                  e.preventDefault();
                  submitPassword();
                }}
              >
                <ScreenHeader
                  title="انتخاب رمز عبور"
                  lead="شماره موبایل شما تأیید شد. برای ورودهای بعدی یک رمز عبور انتخاب کنید."
                />
                <input
                  type="text"
                  name="username"
                  autoComplete="username"
                  value={phone}
                  readOnly
                  hidden
                  tabIndex={-1}
                />
                <div
                  className="sk-card"
                  style={{
                    padding: 24,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 18,
                    maxWidth: 560,
                  }}
                >
                  <div className="sk-field">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <label htmlFor="trial-pw" style={{ margin: 0 }}>
                        رمز عبور
                      </label>
                      <button
                        type="button"
                        className="sk-btn sk-btn--ghost sk-btn--sm"
                        onClick={() => setShowPassword((v) => !v)}
                        style={{ marginInlineStart: 'auto', height: 26 }}
                      >
                        {showPassword ? 'پنهان کردن' : 'نمایش رمز'}
                      </button>
                    </div>
                    <input
                      id="trial-pw"
                      className="sk-input"
                      type={pwInputType}
                      required
                      autoComplete="new-password"
                      minLength={MIN_PASSWORD_LENGTH}
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        setErrPw(false);
                        setErrPw2(false);
                      }}
                      placeholder={`حداقل ${fa(MIN_PASSWORD_LENGTH)} کاراکتر`}
                      style={{ direction: 'ltr', textAlign: 'left', letterSpacing: '.04em' }}
                    />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingTop: 4 }}>
                      <div className="sk-prog" style={{ flex: 1 }}>
                        <div
                          className="sk-prog__fill"
                          style={{ width: `${pwScore * 33.4}%`, background: pwColor }}
                        />
                      </div>
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: pwColor,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {pwLabel}
                      </span>
                    </div>
                    {errPw ? <FieldError>رمز عبور شرایط لازم را ندارد.</FieldError> : null}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {passwordRules.map((rule) => (
                      <div
                        key={rule.label}
                        style={{ display: 'flex', alignItems: 'center', gap: 9 }}
                      >
                        {rule.ok ? (
                          <span
                            style={{
                              width: 18,
                              height: 18,
                              borderRadius: '50%',
                              background: 'var(--sk-success)',
                              color: '#fff',
                              display: 'grid',
                              placeContent: 'center',
                              flex: '0 0 auto',
                              fontSize: 11,
                            }}
                          >
                            <Icon name="check" size="sm" />
                          </span>
                        ) : (
                          <span
                            style={{
                              width: 18,
                              height: 18,
                              borderRadius: '50%',
                              border: '1.5px solid var(--sk-border-strong)',
                              flex: '0 0 auto',
                            }}
                          />
                        )}
                        <span
                          style={{
                            fontSize: 13,
                            color: rule.ok ? 'var(--sk-fg)' : 'var(--sk-fg-subtle)',
                          }}
                        >
                          {rule.label}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="sk-field" style={{ ...dividerTop, paddingTop: 18 }}>
                    <label htmlFor="trial-pw2">تکرار رمز عبور</label>
                    <input
                      id="trial-pw2"
                      className="sk-input"
                      type={pwInputType}
                      required
                      autoComplete="new-password"
                      value={passwordConfirmation}
                      onChange={(e) => {
                        setPasswordConfirmation(e.target.value);
                        setErrPw2(false);
                      }}
                      placeholder="رمز عبور را دوباره وارد کنید"
                      style={{ direction: 'ltr', textAlign: 'left', letterSpacing: '.04em' }}
                    />
                    {errPw2 ? <FieldError>دو رمز وارد‌شده یکسان نیستند.</FieldError> : null}
                    {pwMatched ? (
                      <span style={{ fontSize: 12, color: 'var(--sk-success)' }}>
                        رمز عبور تکرار شد.
                      </span>
                    ) : null}
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      gap: 9,
                      padding: '12px 14px',
                      borderRadius: 'var(--sk-r-md)',
                      background: 'var(--sk-info-soft)',
                      alignItems: 'flex-start',
                    }}
                  >
                    <span
                      style={{ color: '#2f6fa8', display: 'flex', paddingTop: 2, flex: '0 0 auto' }}
                    >
                      <Icon name="shield" size="sm" />
                    </span>
                    <span style={{ fontSize: 12, lineHeight: 1.8, color: 'var(--sk-fg-muted)' }}>
                      برای ورود به فضای کاری از همین شماره موبایل و رمز عبور استفاده می‌کنید.
                    </span>
                  </div>
                </div>
                <div style={actionsStyle}>
                  <button type="submit" className="sk-btn sk-btn--primary sk-btn--lg">
                    ادامه
                  </button>
                  <button
                    type="button"
                    className="sk-btn sk-btn--ghost sk-btn--lg"
                    onClick={() => go('A4')}
                  >
                    بازگشت
                  </button>
                </div>
              </form>
            ) : null}

            {screen === 'A5' ? (
              <form
                noValidate
                style={screenStyle}
                onSubmit={(e) => {
                  e.preventDefault();
                  submitOwner();
                }}
              >
                <ScreenHeader
                  title="اطلاعات شما"
                  lead="این اطلاعات برای حساب مالک سازمان ثبت می‌شود."
                />
                <div
                  className="sk-card sk-onb-grid2"
                  style={{
                    padding: 24,
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 18,
                  }}
                >
                  <div className="sk-field">
                    <label htmlFor="trial-fn">نام</label>
                    <input
                      id="trial-fn"
                      className="sk-input"
                      required
                      autoComplete="given-name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder="رضا"
                    />
                    {ownerErrors['firstName'] ? (
                      <FieldError>{ownerErrors['firstName']}</FieldError>
                    ) : null}
                  </div>
                  <div className="sk-field">
                    <label htmlFor="trial-ln">نام خانوادگی</label>
                    <input
                      id="trial-ln"
                      className="sk-input"
                      required
                      autoComplete="family-name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      placeholder="مهرنیا"
                    />
                    {ownerErrors['lastName'] ? (
                      <FieldError>{ownerErrors['lastName']}</FieldError>
                    ) : null}
                  </div>
                  <div className="sk-field">
                    <label htmlFor="trial-role">سمت سازمانی</label>
                    <input
                      id="trial-role"
                      className="sk-input"
                      value={ownerRole}
                      onChange={(e) => setOwnerRole(e.target.value)}
                      placeholder="مدیرعامل"
                    />
                    <FieldHint>
                      عنوان سازمانی برای نمایش است و سطح دسترسی را تعیین نمی‌کند.
                    </FieldHint>
                  </div>
                </div>
                <div style={actionsStyle}>
                  <button type="submit" className="sk-btn sk-btn--primary sk-btn--lg">
                    ادامه
                  </button>
                  <button
                    type="button"
                    className="sk-btn sk-btn--ghost sk-btn--lg"
                    onClick={() => go('A4b')}
                  >
                    بازگشت
                  </button>
                </div>
              </form>
            ) : null}

            {screen === 'A6' ? (
              <form
                noValidate
                style={screenStyle}
                onSubmit={(e) => {
                  e.preventDefault();
                  submitCompany();
                }}
              >
                <ScreenHeader
                  title="شرکت شما"
                  lead="نام شرکت، نام برند و لوگویی که در محیط سیستم دیده می‌شود."
                />
                <div
                  className="sk-card"
                  style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 18 }}
                >
                  <div
                    className="sk-onb-grid2"
                    style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}
                  >
                    <div className="sk-field">
                      <label htmlFor="trial-company">نام شرکت</label>
                      <input
                        id="trial-company"
                        className="sk-input"
                        required
                        autoComplete="organization"
                        value={company}
                        onChange={(e) => setCompany(e.target.value)}
                        placeholder="ساختار سیستم"
                      />
                      {errCompany ? <FieldError>نام شرکت الزامی است.</FieldError> : null}
                      <FieldHint>نام رسمی یا سازمانی</FieldHint>
                    </div>
                    <div className="sk-field">
                      <label htmlFor="trial-brand">نام برند</label>
                      <input
                        id="trial-brand"
                        className="sk-input"
                        value={brandName}
                        onChange={(e) => setBrandName(e.target.value)}
                        placeholder="ساختار"
                      />
                      <FieldHint>نامی که بیشتر در محیط سیستم نمایش داده می‌شود</FieldHint>
                    </div>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 10,
                      ...dividerTop,
                      paddingTop: 18,
                    }}
                  >
                    <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--sk-fg-muted)' }}>
                      لوگوی شرکت
                    </span>
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept={LOGO_ALLOWED_MIME_TYPES.join(',')}
                      hidden
                      onChange={(e) => {
                        pickLogo(e.target.files?.[0]);
                        e.target.value = '';
                      }}
                    />
                    {logo === null ? (
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => logoInputRef.current?.click()}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            logoInputRef.current?.click();
                          }
                        }}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => {
                          e.preventDefault();
                          pickLogo(e.dataTransfer.files[0]);
                        }}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 7,
                          padding: 26,
                          borderRadius: 'var(--sk-r-md)',
                          border: '1.5px dashed var(--sk-border-strong)',
                          background: 'var(--sk-bg-canvas)',
                          cursor: 'pointer',
                        }}
                      >
                        <span style={{ color: 'var(--sk-fg-subtle)', display: 'flex' }}>
                          <Icon name="upload" size="md" />
                        </span>
                        <span style={{ fontSize: 13, color: 'var(--sk-fg-muted)' }}>
                          فایل را اینجا رها کنید یا برای انتخاب کلیک کنید
                        </span>
                        <span style={{ fontSize: 11, color: 'var(--sk-fg-subtle)' }}>
                          PNG، JPEG، SVG یا WEBP · حداکثر{' '}
                          {fa(Math.floor(LOGO_MAX_BYTES / 1_000_000))} مگابایت
                        </span>
                      </div>
                    ) : (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 14,
                          padding: '16px 18px',
                          borderRadius: 'var(--sk-r-md)',
                          border: '1px solid var(--sk-border)',
                          background: 'var(--sk-bg-canvas)',
                        }}
                      >
                        <div style={tileStyle(52, 15, TILE_BRAND)}>
                          <img
                            src={logo.dataUrl}
                            alt="پیش‌نمایش لوگو"
                            style={{ width: 40, height: 40, objectFit: 'contain' }}
                          />
                        </div>
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
                          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--sk-fg)' }}>
                            لوگوی شرکت
                          </span>
                          <span style={{ fontSize: 12, color: 'var(--sk-fg-subtle)' }}>
                            {fa(Math.max(1, Math.round(logo.bytes / 1024)))} کیلوبایت · انتخاب شد
                          </span>
                        </div>
                        <button
                          type="button"
                          className="sk-btn sk-btn--secondary sk-btn--sm"
                          onClick={() => logoInputRef.current?.click()}
                        >
                          تغییر
                        </button>
                        <button
                          type="button"
                          className="sk-btn sk-btn--ghost sk-btn--sm"
                          onClick={() => setLogo(null)}
                        >
                          حذف
                        </button>
                      </div>
                    )}
                    {logoError ? <FieldError>{logoError}</FieldError> : null}
                  </div>
                </div>
                <div style={actionsStyle}>
                  <button type="submit" className="sk-btn sk-btn--primary sk-btn--lg">
                    ادامه
                  </button>
                  <button
                    type="button"
                    className="sk-btn sk-btn--ghost sk-btn--lg"
                    onClick={() => go('A5')}
                  >
                    بازگشت
                  </button>
                </div>
              </form>
            ) : null}

            {screen === 'A7' ? (
              <form
                noValidate
                style={screenStyle}
                onSubmit={(e) => {
                  e.preventDefault();
                  if (slugCheck === 'available') go('A8');
                }}
              >
                <ScreenHeader
                  title="آدرس فضای کاری"
                  lead="یک آدرس اختصاصی برای ورود به ERP شرکت خود انتخاب کنید."
                />
                <div
                  className="sk-card"
                  style={{
                    padding: 24,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 14,
                    maxWidth: 600,
                  }}
                >
                  <div className="sk-field">
                    <label htmlFor="trial-slug">آدرس اختصاصی</label>
                    <div className="sk-input-group" style={{ direction: 'ltr' }}>
                      <span
                        style={{ fontSize: 13, color: 'var(--sk-fg-subtle)', whiteSpace: 'nowrap' }}
                      >
                        {SLUG_PREFIX}
                      </span>
                      <input
                        id="trial-slug"
                        required
                        autoCapitalize="none"
                        spellCheck={false}
                        value={slug}
                        onChange={(e) => setSlug(e.target.value.trim().toLowerCase())}
                        placeholder="sakhtar"
                        style={{ direction: 'ltr', textAlign: 'left', fontWeight: 600 }}
                      />
                      {slugCheck === 'checking' ? (
                        <span
                          className="sk-spin"
                          style={{ width: 15, height: 15, flex: '0 0 auto' }}
                        />
                      ) : null}
                      {slugCheck === 'available' ? (
                        <span style={{ color: 'var(--sk-success)', display: 'flex' }}>
                          <Icon name="check" size="sm" />
                        </span>
                      ) : null}
                      {slugCheck === 'taken' ? (
                        <span style={{ color: 'var(--sk-danger)', display: 'flex' }}>
                          <Icon name="x" size="sm" />
                        </span>
                      ) : null}
                    </div>
                  </div>
                  {slugMsg ? (
                    <span style={{ fontSize: 12, lineHeight: 1.8, color: slugMsgColor }}>
                      {slugMsg}
                    </span>
                  ) : null}
                  {slugCheck === 'taken' && slugSuggestions.length > 0 ? (
                    <div
                      style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}
                    >
                      <span style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>پیشنهاد:</span>
                      {slugSuggestions.map((s) => (
                        <button
                          key={s}
                          type="button"
                          className="sk-btn sk-btn--secondary sk-btn--sm"
                          style={{ direction: 'ltr' }}
                          onClick={() => setSlug(s)}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  ) : null}
                  <div
                    style={{
                      ...dividerTop,
                      paddingTop: 16,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 5,
                    }}
                  >
                    <span style={{ fontSize: 12, color: 'var(--sk-fg-subtle)' }}>
                      آدرس فضای کاری شما:
                    </span>
                    <span
                      style={{
                        fontSize: 15,
                        fontWeight: 600,
                        color: 'var(--sk-fg)',
                        direction: 'ltr',
                        textAlign: 'right',
                      }}
                    >
                      {SLUG_PREFIX}
                      {slug || '…'}
                    </span>
                  </div>
                  <FieldHint>فقط حروف انگلیسی کوچک، عدد و خط تیره</FieldHint>
                </div>
                <div style={actionsStyle}>
                  <button
                    type="submit"
                    className="sk-btn sk-btn--primary sk-btn--lg"
                    disabled={slugCheck !== 'available'}
                  >
                    ادامه
                  </button>
                  <button
                    type="button"
                    className="sk-btn sk-btn--ghost sk-btn--lg"
                    onClick={() => go('A6')}
                  >
                    بازگشت
                  </button>
                </div>
              </form>
            ) : null}

            {screen === 'A8' ? (
              <form
                noValidate
                style={screenStyle}
                onSubmit={(e) => {
                  e.preventDefault();
                  void createWorkspace();
                }}
              >
                <ScreenHeader
                  title="درباره شرکت"
                  lead="این پاسخ‌ها تنظیمات پیشنهادی سیستم را شکل می‌دهند. ماژول‌های محصول ثابت هستند و با این انتخاب کم و زیاد نمی‌شوند."
                />
                <div
                  className="sk-card"
                  style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div
                      style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}
                    >
                      <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--sk-fg-muted)' }}>
                        زمینه فعالیت
                      </span>
                      <span style={{ fontSize: 12, color: 'var(--sk-fg-subtle)' }}>
                        هر تعداد که مرتبط است انتخاب کنید
                      </span>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {INDUSTRY_OPTIONS.map((option) => {
                        const on = industries.includes(option.code);
                        return (
                          <button
                            key={option.code}
                            type="button"
                            aria-pressed={on}
                            onClick={() =>
                              setIndustries((prev) =>
                                on ? prev.filter((c) => c !== option.code) : [...prev, option.code],
                              )
                            }
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              height: 36,
                              padding: '0 14px',
                              borderRadius: 'var(--sk-r-md)',
                              border: `1.5px solid ${on ? 'var(--sk-brand-500)' : 'var(--sk-border)'}`,
                              background: on
                                ? 'color-mix(in oklch, var(--sk-brand-500), transparent 92%)'
                                : 'var(--sk-bg-elev)',
                              color: on ? 'var(--sk-brand-700)' : 'var(--sk-fg-muted)',
                              fontFamily: 'inherit',
                              fontSize: 13,
                              fontWeight: 500,
                              cursor: 'pointer',
                            }}
                          >
                            <span
                              style={{
                                width: 16,
                                height: 16,
                                borderRadius: 5,
                                border: `1.5px solid ${on ? 'var(--sk-brand-500)' : 'var(--sk-border-strong)'}`,
                                background: on ? 'var(--sk-brand-500)' : 'transparent',
                                color: '#fff',
                                display: 'grid',
                                placeContent: 'center',
                                flex: '0 0 auto',
                                fontSize: 11,
                              }}
                            >
                              {on ? <Icon name="check" size="sm" /> : null}
                            </span>
                            {option.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 10,
                      ...dividerTop,
                      paddingTop: 18,
                    }}
                  >
                    <label
                      htmlFor="trial-members"
                      style={{ fontSize: 13, fontWeight: 500, color: 'var(--sk-fg-muted)' }}
                    >
                      تعداد اعضای همکاران شما (کسانی که از این سیستم استفاده خواهند کرد)
                    </label>
                    <div
                      style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}
                    >
                      <div className="sk-input-group" style={{ width: 180 }}>
                        <input
                          id="trial-members"
                          required
                          inputMode="numeric"
                          value={fa(memberCount)}
                          onChange={(e) => {
                            setMemberCount(
                              toEnglishDigits(e.target.value).replace(/\D/g, '').slice(0, 6),
                            );
                            setErrMembers(false);
                          }}
                          placeholder="۱۲"
                          style={{ fontWeight: 600 }}
                        />
                        <span
                          style={{
                            fontSize: 13,
                            color: 'var(--sk-fg-subtle)',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          نفر
                        </span>
                      </div>
                      {errMembers ? <FieldError>تعداد اعضا را وارد کنید.</FieldError> : null}
                    </div>
                  </div>
                </div>
                <div style={actionsStyle}>
                  <button type="submit" className="sk-btn sk-btn--primary sk-btn--lg">
                    ایجاد فضای کاری
                  </button>
                  <button
                    type="button"
                    className="sk-btn sk-btn--ghost sk-btn--lg"
                    onClick={() => go('A7')}
                  >
                    بازگشت
                  </button>
                </div>
              </form>
            ) : null}

            {screen === 'A9' ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 22,
                  paddingTop: 56,
                  maxWidth: 560,
                  alignSelf: 'center',
                  width: '100%',
                }}
              >
                {provFailed ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <span style={tileStyle(46, 23, TILE_DANGER)}>
                        <Icon name="x" size="md" />
                      </span>
                      <h1 style={{ ...h1Style, fontSize: 24 }}>ساخت فضای کاری کامل نشد</h1>
                      <p style={{ ...leadStyle, lineHeight: 2 }}>{provFailed}</p>
                    </div>
                    <div style={{ ...actionsStyle, flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        className="sk-btn sk-btn--primary sk-btn--lg"
                        onClick={() => void createWorkspace()}
                      >
                        تلاش دوباره
                      </button>
                      <button
                        type="button"
                        className="sk-btn sk-btn--ghost sk-btn--lg"
                        onClick={() => go('A8')}
                      >
                        بازگشت و بررسی اطلاعات
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                    <h1 style={{ ...h1Style, fontSize: 24 }}>در حال آماده‌سازی فضای کار شما</h1>
                    <div
                      className="sk-card"
                      style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 2 }}
                    >
                      {PROVISION_STEPS.map((label, i) => (
                        <div
                          key={label}
                          style={{ display: 'flex', alignItems: 'center', gap: 12, height: 46 }}
                        >
                          <StepDot state={i === 0 ? 'running' : 'pending'} />
                          <span
                            style={{
                              fontSize: 14,
                              fontWeight: i === 0 ? 600 : 400,
                              color: i === 0 ? 'var(--sk-fg)' : 'var(--sk-fg-subtle)',
                            }}
                          >
                            {label}
                          </span>
                        </div>
                      ))}
                    </div>
                    <span style={{ fontSize: 12, color: 'var(--sk-fg-subtle)' }}>
                      این مرحله معمولاً کمتر از یک دقیقه طول می‌کشد. صفحه را نبندید.
                    </span>
                  </div>
                )}
              </div>
            ) : null}

            {screen === 'A10' && created ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 24,
                  paddingTop: 44,
                  maxWidth: 600,
                  alignSelf: 'center',
                  width: '100%',
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <span style={tileStyle(50, 25, TILE_SUCCESS)}>
                    <Icon name="check" size="md" />
                  </span>
                  <h1 style={{ ...h1Style, fontSize: 28 }}>فضای کاری شما ساخته شد</h1>
                  <p style={{ ...leadStyle, fontSize: 15, lineHeight: 2 }}>
                    دوره آزمایشی {fa(LAUNCH_TRIAL_DURATION_DAYS)} روزه شما از همین لحظه آغاز شد.
                  </p>
                </div>
                <div
                  className="sk-card"
                  style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 16 }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                    <div style={tileStyle(46, 14, TILE_BRAND)}>
                      {logo ? (
                        <img
                          src={logo.dataUrl}
                          alt=""
                          style={{ width: 34, height: 34, objectFit: 'contain' }}
                        />
                      ) : (
                        <Icon name="layers" size="md" />
                      )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--sk-fg)' }}>
                        {created.displayName}
                      </span>
                      <span
                        style={{ fontSize: 12, color: 'var(--sk-fg-subtle)', direction: 'ltr' }}
                      >
                        {SLUG_PREFIX}
                        {created.slug}
                      </span>
                    </div>
                    <span
                      className="sk-badge sk-badge--success"
                      style={{ marginInlineStart: 'auto' }}
                    >
                      دوره آزمایشی فعال
                    </span>
                  </div>
                  <div
                    className="sk-onb-grid2"
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: 14,
                      ...dividerTop,
                      paddingTop: 16,
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      <span style={{ fontSize: 12, color: 'var(--sk-fg-subtle)' }}>
                        پایان دوره آزمایشی
                      </span>
                      <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--sk-fg)' }}>
                        {created.trialEndsLabel}
                      </span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      <span style={{ fontSize: 12, color: 'var(--sk-fg-subtle)' }}>
                        مالک سازمان
                      </span>
                      <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--sk-fg)' }}>
                        {created.ownerName}
                      </span>
                    </div>
                  </div>
                </div>
                <div style={actionsStyle}>
                  <button
                    type="button"
                    className="sk-btn sk-btn--primary sk-btn--lg"
                    onClick={() => navigate(created.nextPath)}
                  >
                    شروع تنظیم سیستم
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
